import { Text } from '@mantine/core'
import type { PermissionApiDTO } from '@testsa-com/common/src/codegen/generated/Api.schemas'
import type { TFunction } from 'i18next'
import type { RoleCategory } from '../types/Users.type'

export const UsersHelper = {
  ADMIN_ROLE: 'admin',
  SUPER_ADMIN_ROLE: 'super_admin',
  CLIENTUSER_PERMISSIONS: ['canAddOtherClientUsers'],
  SUPERADMIN_PERMISSIONS: ['projects.admin', 'projects.delete'] satisfies PermissionApiDTO[],
  ROLES_PERMISSIONS: [
    {
      group: 'projects',
      items: [
        'projects.edit',
        'projects.users.edit',
        'projects.pollsters.contact',
        'projects.pollsters.export',
        'projects.pollsters.edit',
        'projects.assignments.manage',
        'projects.contracts.manage',
        'projects.briefings.manage',
        'projects.briefings.contact',
        'projects.attachments.manage',
        'projects.clockings.edit',
        'projects.assignments.export',
        'projects.programs.export',
        'projects.vacations.export',
        'projects.clockings.export',
        'projects.tallies.export',
        'projects.panelSheets.export',
        'projects.list',
        'projects.create',
        'projects.admin',
        'projects.delete',
      ],
    },
    {
      group: 'pollsters',
      items: [
        'pollsters.list',
        'pollsters.admin_data',
        'pollsters.create',
        'pollsters.edit',
        'pollsters.delete',
        'pollsters.export',
        'pollsters.import',
        'pollsters.documents.remind',
        'pollsters.availability.remind',
        'pollsters.notifications.send',
      ],
    },
    {
      group: 'equipments',
      items: [
        'equipments.list',
        'equipments.edit_status',
        'equipments.enroll',
        'equipments.export',
        'equipments.import',
        'equipments.assign',
      ],
    },
    {
      group: 'users',
      items: ['users.list', 'users.create', 'users.edit', 'users.delete'],
    },
    {
      group: 'clients',
      items: [
        'clients.organizations.view',
        'clients.organizations.edit',
        'clients.organizations.delete',
        'clients.reports.view',
        'clients.reports.edit',
        'clients.reports.delete',
        'clients.files.view',
        'clients.files.edit',
        'clients.files.delete',
        'clients.users.view',
        'clients.users.edit',
        'clients.users.delete',
      ],
    },
    {
      group: 'hr',
      items: [
        'hr.contracts.view',
        'hr.contracts.export',
        'hr.contracts.manage',
        'hr.dashboard.view',
      ],
    },
    {
      group: 'payroll',
      items: [
        'payroll.payables.view',
        'payroll.payouts.view',
        'payroll.payouts.dispatch',
        'payroll.rates.view',
        'payroll.rates.edit',
      ],
    },
    {
      group: 'field',
      items: [
        'field.access',
        'field.recruit',
        'field.contact',
        'field.briefings.generate',
        'field.briefings.hire',
        'field.briefings.presence',
        'field.surveys.reassign',
        'field.surveys.presence',
        'field.equipment.restitution',
      ],
    },
    {
      group: 'chat',
      items: ['chat.access'],
    },
    {
      group: 'panelPuchSheets',
      items: ['panelPuchSheets.view', 'panelPuchSheets.edit'],
    },
  ] satisfies Array<{ group: RoleCategory; items: PermissionApiDTO[] }>,
  permissionsSync: (permissions: PermissionApiDTO[]) => {
    /* Add list permission if edit permissions clicked */
    for (const category of [
      'projects',
      'pollsters',
      'equipments',
      'users',
      'roles',
    ] as RoleCategory[]) {
      if (
        (permissions.includes(`${category}.edit` as PermissionApiDTO) ||
          permissions.includes(`${category}.admin` as PermissionApiDTO) ||
          permissions.includes(`${category}.admin_data` as PermissionApiDTO) ||
          permissions.includes(`${category}.export` as PermissionApiDTO) ||
          permissions.includes(`${category}.import` as PermissionApiDTO) ||
          permissions.includes(`${category}.delete` as PermissionApiDTO)) &&
        !permissions.includes(`${category}.list` as PermissionApiDTO)
      ) {
        permissions.push(`${category}.list` as PermissionApiDTO)
      }
    }

    /* Add view permission if edit permissions clicked */
    for (const category of [
      'clients.organizations',
      'clients.reports',
      'clients.files',
      'clients.users',
      'hr.contracts',
      'panelPuchSheets',
    ] as RoleCategory[]) {
      if (
        (permissions.includes(`${category}.edit` as PermissionApiDTO) ||
          permissions.includes(`${category}.export` as PermissionApiDTO) ||
          permissions.includes(`${category}.manage` as PermissionApiDTO) ||
          permissions.includes(`${category}.delete` as PermissionApiDTO)) &&
        !permissions.includes(`${category}.view` as PermissionApiDTO)
      ) {
        permissions.push(`${category}.view` as PermissionApiDTO)
      }
    }

    /* Add access permission if edit permissions clicked */
    for (const category of ['field'] as RoleCategory[]) {
      if (
        (permissions.includes(`${category}.recruit` as PermissionApiDTO) ||
          permissions.includes(`${category}.contact` as PermissionApiDTO)) &&
        !permissions.includes(`${category}.access` as PermissionApiDTO)
      ) {
        permissions.push(`${category}.access` as PermissionApiDTO)
      }
    }

    return permissions
  },
  getPermissionsList: (_permissions: PermissionApiDTO[], tc: TFunction<'common'>): JSX.Element => {
    if (_permissions[0] === '*') {
      return (
        <Text fs="italic" c="gray">
          {tc('words.all_female')}
        </Text>
      )
    }

    return (
      <Text tt="lowercase">
        {_permissions.length} {tc('words.permission_other')}
      </Text>
    )
  },
}
